import { useNavigate } from "react-router-dom";
import { getRoleBasedProperty } from "@components/RoleBasedProperty";
import { Constant } from "@constants/index";
import { HTTPMethods } from "@enums/HttpMethods";
import { RequestContentType } from "@enums/RequestContentType";
import { RequestOptionType } from "@interfaces/Request";
import { logOut } from "@slices/authSlice";
import { setRoleBasedProperty } from "@slices/roleBasedPropertySlice";
import { useAppDispatch } from "@store/hooks";
import { getFromStorage } from "@utilities/storageService";
import { ErrorMessages } from "@constants/errorMessages";
import { responseStatusCode } from "@constants/responseStatusCode";

export class APICaller {
    private abortController: AbortController | null;
    private handleLogout: () => void;

    constructor(handleLogout: () => void) {
        this.abortController = null;
        this.handleLogout = handleLogout;
    }

    async validateAndCallAPI(
        APIUrl: string,
        method: HTTPMethods,
        body?: object | null,
        contentDataType: RequestContentType = RequestContentType.JSON,
        header?: object | null,
        forceCache?: boolean
    ) {
        if (this.abortController) {
            this.abortController.abort(ErrorMessages.APIAbortError);
        }
        this.abortController = new AbortController();

        const response = await this.callAPI(APIUrl, method, contentDataType, body, header, forceCache, this.abortController.signal);

        return response;
    }

    private async callAPI(
        APIUrl: string,
        method: HTTPMethods,
        contentDataType: RequestContentType,
        body?: object | null,
        header?: object | null,
        forceCache?: boolean,
        abortSignal?: AbortSignal
    ) {
        let response: Response;

        let auth = getFromStorage("auth");
        const token = auth ? JSON.parse(auth).authToken : "";

        let options: RequestOptionType = {
            method: method,
            cache: forceCache ? "force-cache" : "no-cache",
            headers: {},
            body: null,
            signal: abortSignal,
        };
        switch (contentDataType) {
            case RequestContentType.JSON:
                options.headers = {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...header,
                };
                options.body = body == null ? null : JSON.stringify(body);
                break;
            case RequestContentType.FORM:
                options.headers = {
                    ...header,
                };
                options.body = body == null ? null : body;
                break;
        }

        if (token) {
            options.headers.Authorization = `Bearer ${token}`;
        }

        response = await this.appFetch(APIUrl, options);
        const jsonResponse = await response.json();

        if (response.status === responseStatusCode.UNAUTHORIZED) {
            this.handleLogout();
        }

        return { jsonResponse };
    }

    private async appFetch(APIUrl: string, options: RequestOptionType) {
        try {
            return await fetch(APIUrl, options);
        } catch (err: any) {
            // eslint-disable-next-line
            throw {
                success: false,
                message: String(err) === ErrorMessages.APIAbortError ? ErrorMessages.APIAbortError : Constant.SERVER_ERROR,
                data: null,
            };
        }
    }
}

export const useAPICaller = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const handleLogout = () => {
        dispatch(logOut());
        dispatch(setRoleBasedProperty(getRoleBasedProperty()));
        navigate("/login");
    };

    return new APICaller(handleLogout);
};
